import React, { useCallback, useEffect, useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
    getAllUsers,
    getCompaniesName,
    getAllProjects as getAllProjectsSuperAdmin
} from '../../services/api/implementation/impl';
import {
    getAllProjects,
    getCloseDeals
} from '../../../builder/services/api/implementation/impl';

import Layout from './AppLayout';
import Header from '../../../shared/components/layout/Header';
import GridWrapper from '../../../shared/components/layout/GridWrapper';
import Table from '../../../shared/components/layout/Table';
import Search from '../../../shared/components/ui/Search';
import toast from 'react-hot-toast';
import { transformData } from '../../utils/transformData';
import PaginationComponent from '../../../shared/components/PaginationComponent';
import mixpanel from 'mixpanel-browser';
import Cookies from 'js-cookie';

import { useNavigate } from 'react-router-dom';

const ClosedDeals = () => {
    const navigate = useNavigate();
    // Removed filteredData state as we use server-side search now
    const [filteredData, setFilteredData] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [page, setPage] = useState(1);
    const [limit] = useState(10);

    const userId = Cookies.get('userId');

    // Page view tracking
    useEffect(() => {
        mixpanel.track('Super Admin Closed Deals Viewed', {
            user_id: userId,
            page: page,
            timestamp: new Date().toISOString()
        });
    }, [userId, page]);

    // Fetch builders from /builder endpoint
    const {
        data: builders,
        refetch: refetchBuilders,
        isFetching
    } = useQuery({
        queryKey: ['builders'], // Fetch once (or invalidate manually), no page dependency
        queryFn: () => getAllUsers(1, 10000), // Fetch ALL users
        keepPreviousData: true,
        onSuccess: (result) => {
            if (
                result?.response?.status ||
                result?.message?.includes('Error') ||
                result?.name === 'AxiosError'
            ) {
                mixpanel.track('Super Admin Builders Fetch Failed', {
                    user_id: userId,
                    page: page,
                    limit: limit,
                    error_message: result?.response?.data?.message || result?.message || 'Unknown error',
                    timestamp: new Date().toISOString()
                });
                toast.error(result?.response?.data?.message || result?.message || 'Failed to fetch builders');
                return;
            }

            mixpanel.track('Super Admin Builders Fetch Success', {
                user_id: userId,
                page: page,
                limit: limit,
                builders_count: result?.data?.length || 0,
                total_builders: result?.meta?.total || 0,
                timestamp: new Date().toISOString()
            });
        },
        onError: (err) => {
            mixpanel.track('Super Admin Builders Fetch Failed', {
                user_id: userId,
                page: page,
                limit: limit,
                error_message: err?.response?.data?.message || err?.message || 'Unknown error',
                timestamp: new Date().toISOString()
            });
            toast.error(err?.response?.data?.message || err?.message || 'Failed to fetch builders');
        }
    });

    // Fetch all projects
    const { data: projects } = useQuery({
        queryKey: ['allProjects'],
        queryFn: () => getAllProjects(),
        onError: (err) => {
            console.error('Error fetching projects:', err);
        }
    });

    // Fetch companies for company names
    const { data: companiesList } = useQuery({
        queryKey: ['allCompaniesList'],
        queryFn: () => getCompaniesName(),
        onError: (err) => {
            console.error('Error fetching companies list:', err);
        }
    });

    // Fetch all closed deals
    const { data: closedDealsData } = useQuery({
        queryKey: ['allClosedDeals'],
        queryFn: () => getCloseDeals(null, 1, 9999),
        onError: (err) => {
            console.error('Error fetching closed deals:', err);
        }
    });

    // Total pages - handled dynamically now
    // const totalPages = builders?.meta ? Math.ceil(builders?.meta.total / limit) : 1; 

    // Process builders data with projects and deals count
    const processedBuilders = useMemo(() => {
        if (!builders?.data || !projects || !closedDealsData?.data || !companiesList) return [];

        return builders.data.map((builder) => {
            // Find all companies for this builder
            const builderCompanies = companiesList.filter(c => c.builder_id === builder.id);
            const companyNames = builderCompanies.length > 0
                ? builderCompanies.map(c => c.company_name).join(', ')
                : (builder.name || 'N/A');

            // Count projects for this builder
            // User requested to filter by builder_id
            const builderProjects = projects.filter((p) => p.builder_id === builder.id);

            const projectsCount = builderProjects.length;

            // Count closed deals for this builder
            // Match via nested builder_id in deal -> unit -> project
            const closedDealsCount = closedDealsData.data.filter((deal) =>
                deal?.unit?.project?.builder_id === builder.id
            ).length;

            return {
                id: builder.id,
                builder_name: builder.primery_user?.name || '--',
                company_name: companyNames,
                projects_count: projectsCount,
                closed_deals_count: closedDealsCount
            };
        });
    }, [builders?.data, projects, closedDealsData, companiesList]);

    // Set filtered data on initial load or when data changes
    useEffect(() => {
        if (processedBuilders.length > 0 && !searchQuery) {
            setFilteredData(processedBuilders);
        }
    }, [processedBuilders, searchQuery]);
    // Set filtered data on initial load

    // Handle search
    const handleSearchChange = useCallback(
        (incomingQuery) => {
            mixpanel.track('Super Admin Builders Search', {
                user_id: userId,
                search_query: incomingQuery,
                timestamp: new Date().toISOString()
            });

            setSearchQuery(incomingQuery);
            setPage(1);

            if (incomingQuery) {
                const lowerCaseQuery = incomingQuery.toLowerCase();
                const filtered = processedBuilders.filter(
                    (builder) =>
                        builder.builder_name?.toLowerCase().includes(lowerCaseQuery) ||
                        builder.company_name?.toLowerCase().includes(lowerCaseQuery)
                );
                setFilteredData(filtered || []);
            } else {
                setFilteredData(processedBuilders || []);
            }
        },
        [processedBuilders, userId]
    );

    // Table columns
    const buildersDataFormat = ['id', 'builder_name', 'company_name', 'projects_count', 'closed_deals_count'];

    // Pagination Logic
    const startIndex = (page - 1) * limit;
    const paginatedData = filteredData.slice(startIndex, startIndex + limit);

    // Transform for table consumption (use paginatedData)
    const transformedData = paginatedData.map((el) =>
        transformData(el, buildersDataFormat)
    );
    // Total pages based on filtered data
    const totalPages = Math.ceil(filteredData.length / limit);

    // Handle pagination
    const handlePageChange = (newPage) => {
        mixpanel.track('Super Admin Builders Pagination', {
            user_id: userId,
            from_page: page,
            to_page: newPage,
            timestamp: new Date().toISOString()
        });
        setPage(newPage);
    };

    // Handle row click
    const handleRowClick = (row) => {
        mixpanel.track('Super Admin Builder Row Click', {
            user_id: userId,
            builder_id: row,
            timestamp: new Date().toISOString()
        });

        // Find the builder data to check for companies
        // 'row' is just the ID here
        const builderData = processedBuilders.find(b => b.id === row);

        // Check if company_name indicates valid companies (it will be comma separated list or N/A)
        // A better check would be to see if we have actual companies in companiesList for this builder
        const hasCompanies = companiesList.some(c => c.builder_id === row);

        if (hasCompanies) {
            mixpanel.track('Super Admin Builder Row Click', {
                user_id: userId,
                builder_id: row,
                builder_name: builderData?.builder_name,
                timestamp: new Date().toISOString()
            });
            navigate(`/super-admin/closed-deals/${row}/companies`);
        } else {
            toast.error('No companies found for this builder');
        }
    };

    return (
        <>
            <Layout>
                {/* Top Header */}
                <div className="fixed z-[20] top-0 left-[16.5vw] w-[83.5vw]">
                    <Header data={{ name: 'Closed Deals' }} />
                </div>

                {/* Search */}
                <div className="flex items-center justify-between px-6 py-4 bg-white fixed top-[4rem] z-20 border-t-[2px] border-stone-100 w-[83vw] shadow-sm">
                    <Search query={searchQuery} onChange={handleSearchChange} />
                </div>

                {/* Main Content - Table + Pagination */}
                <GridWrapper width={100}>
                    <div className="mt-[10rem] relative user-table-container h-[calc(100vh-11.5rem)] overflow-auto w-full">
                        <style>{`
              .user-table-container .table-body {
                position: static !important;
                overflow-y: visible !important;
                max-height: none !important;
                height: auto !important;
                top: auto !important;
                padding-bottom: 0 !important;
              }
              .user-table-container .table-container {
                height: auto !important;
              }
              .user-table-container > div {
                height: auto !important;
              }
            `}</style>
                        <Table
                            headers={['S.No', 'Builder Name', 'Company Name', 'Number of Projects', 'Number of Closed Deals']}
                            data={transformedData?.map((item, index) => ({
                                serialNumber: index + 1,
                                ...item
                            }))}
                            identifier={'id'}
                            handleRowClick={handleRowClick}
                        />

                        <PaginationComponent
                            page={page}
                            totalPages={totalPages}
                            onPageChange={handlePageChange}
                            isFetching={isFetching}
                        />
                    </div>
                </GridWrapper>
            </Layout>
        </>
    );
};

export default ClosedDeals;
